<template>
  <div id="container">
    <TheNavbar />
    
    <div class="accordion-container">
      <div class="title">Medical Imaging</div>
      
      <div class="accordion-section collapsed" id="theory-section">
        <div class="accordion-header" @click="expandSection('theory')">
          <span class="triangle">&#9654;</span>
          <span class="title">Theoretical Foundation</span>
        </div>
        <div class="accordion-content" style="display:none;">
          <p>Medical imaging is built upon several physical and mathematical principles:</p>
          <ul>
            <li><b>X-ray Physics</b>: Uses ionizing radiation. Dense tissues like bones absorb more X-rays and appear brighter on film.</li>
            <li><b>Magnetic Resonance Imaging (MRI)</b>: Based on nuclear magnetic resonance. Uses strong magnetic fields and radio waves to visualize soft tissues.</li>
            <li><b>Ultrasound Theory</b>: Utilizes high-frequency sound waves that reflect off internal structures to create real-time images.</li>
            <li><b>Computed Tomography (CT)</b>: Combines multiple X-ray images taken from different angles to produce cross-sectional slices.</li>
            <li><b>Image Processing & Reconstruction</b>: Mathematical algorithms convert raw sensor data into interpretable images using methods like the Fourier transform or filtered back projection.</li>
          </ul>
        </div>
      </div>
      
      <div class="accordion-section collapsed" id="equipment-section">
        <div class="accordion-header" @click="expandSection('equipment')">
          <span class="triangle">&#9654;</span>
          <span class="title">Equipment</span>
        </div>
        <div class="accordion-content" style="display:none;">
          <p>Medical imaging systems vary based on the imaging technique:</p>
          <ul>
            <li><b>X-ray Systems</b> – standard radiographs and fluoroscopy units</li>
            <li><b>MRI Machines</b> – superconducting magnets, radiofrequency coils</li>
            <li><b>Ultrasound Devices</b> – transducers/probes for 2D and 3D imaging</li>
            <li><b>CT Scanners</b> – rotating X-ray tubes and detectors</li>
            <li><b>PET/SPECT Scanners</b> – use radioactive tracers to visualize metabolic activity</li>
            <li><b>Workstations & PACS</b> – software platforms for viewing, storing, and sharing images</li>
          </ul>
        </div>
      </div>
      
      <div class="accordion-section collapsed" id="applications-section">
        <div class="accordion-header" @click="expandSection('applications')">
          <span class="triangle">&#9654;</span>
          <span class="title">Applications</span>
        </div>
        <div class="accordion-content" style="display:none;">
          <ul>
            <li><b>Diagnosis & Screening</b>: Detecting fractures, tumors, infections, and internal bleeding; Screening for cancers, stroke, and neurological conditions</li>
            <li><b>Surgical & Treatment Planning</b>: Pre-operative planning for precision surgery; Guiding biopsies, catheter insertions, and radiation therapy</li>
            <li><b>Monitoring & Follow-Up</b>: Tracking disease progression or treatment effectiveness; Monitoring pregnancies and fetal development</li>
            <li><b>Research & Innovation</b>: Functional imaging of brain activity; Personalized medicine and AI-driven diagnostics</li>
          </ul>
        </div>
      </div>
    </div>
    
    <!-- <TheFooter /> -->
  </div>
</template>

<script>
import TheNavbar from '@/components/TheNavbar.vue'
import TheFooter from '@/components/TheFooter.vue'

export default {
  name: 'MedicalImaging',
  components: {
    TheNavbar,
    TheFooter
  },
  methods: {
    expandSection(section) {
      const current = document.getElementById(section + '-section');
      const isExpanded = current.classList.contains('expanded');
      
      // 先收起所有
      document.querySelectorAll('.accordion-section').forEach(sec => {
        sec.classList.add('collapsed');
        sec.classList.remove('expanded');
        sec.querySelector('.accordion-content').style.display = 'none';
        sec.querySelector('.triangle').innerHTML = '&#9654;'; // ▶
      });
      
      // 如果当前是收起的，则展开；如果是展开的，则保持收起
      if (!isExpanded) {
        current.classList.remove('collapsed');
        current.classList.add('expanded');
        current.querySelector('.accordion-content').style.display = '';
        current.querySelector('.triangle').innerHTML = '&#9660;'; // ▼
      }
    }
  }
}
</script>

<style scoped>
.accordion-container {
  padding: 32px;
  max-width: 1200px;
  width: 100%;
  margin: 32px auto;
  background: #f8fafc;
  border-radius: 16px;
  box-shadow: 0 4px 24px rgba(60, 80, 120, 0.08);
  color: #263238;
  font-family: 'Segoe UI', 'Arial', sans-serif;
}

.accordion-container > .title {
  font-size: 2.3em;
  font-weight: 700;
  text-align: center;
  margin-bottom: 32px;
  color: #2A439A;
  letter-spacing: 1px;
}

.accordion-section {
  margin-bottom: 16px;
  border-radius: 10px;
  background: #fff;
  box-shadow: 0 2px 8px rgba(60, 80, 120, 0.04);
  border: 1px solid #e3e8f0;
  transition: box-shadow 0.2s;
}

.accordion-section.expanded {
  box-shadow: 0 4px 16px rgba(60, 80, 120, 0.10);
  border-color: #263238;
}

.accordion-header {
  cursor: pointer;
  padding: 18px 28px;
  font-size: 1.25em;
  display: flex;
  align-items: center;
  font-weight: 600;
  color: #2A439A;
  border-radius: 10px 10px 0 0;
  background: #eee;
  transition: background 0.2s;
}

.accordion-header:hover {
  background: #eee;
}

.accordion-header .triangle {
  margin-right: 14px;
  font-size: 1.3em;
  transition: transform 0.2s;
}

.accordion-section.expanded .accordion-header .triangle {
  transform: rotate(90deg);
}

.accordion-header .title {
  font-size: 1.2em;
  font-weight: 700;
  letter-spacing: 0.5px;
}

.accordion-content {
  padding: 18px 32px 18px 48px;
  background: #f5faff;
  border-radius: 0 0 10px 10px;
  color: #263238;
  font-size: 1.08em;
  line-height: 1.7;
  border-top: 1px solid #e3e8f0;
}

.accordion-content ul {
  margin: 0 0 0 1.2em;
  padding: 0;
}

.accordion-content li {
  margin-bottom: 0.7em;
}

.collapsed .accordion-content {
  display: none;
}
</style>


